import React, { useEffect, useReducer, useRef, useState } from 'react'
import {
  useAccount,
  useSignMessage,
  useWaitForTransactionReceipt,
  useWriteContract
} from 'wagmi'
import { useTranslation } from 'react-i18next'
import { useBalance } from 'wagmi'
import { parseEther } from 'viem'
import { useLocation, useNavigate } from 'react-router-dom'

import CommunitySale from '../components/communitySale/CommunitySale'
import {
  WALLET_ACCOUNT_STATUS,
  buildTransactionErrorMessage
} from '../util/walletConnect.helpers'
import {
  // mainReducer,
  // mainInitialState,
  MAIN_PROFILE_DISPATCH
} from '../store/reducers/main'
import { changeMainState } from '../store/actions/main'
import DepositPopup from '../components/popups/DepositPopup'
import {
  checkValidations,
  maxValueRule,
  positiveDecimalRule,
  requiredRule,
  UUIDRule
} from '../util/validation.helpers'
import { containsNoValues } from '../util/object.helpers'
import { openBlankRedirectPage } from '../util/app.helpers'
import {
  getChain,
  wagmiContractConfig
} from '../components/walletConnect/wagmiConfig'
import Loading from '../components/shared/loading/Loading'
import {
  claimStakeAPI,
  getRecentDepositsAPI,
  // getClaimStatusAPI,
  shareXPostUrl
} from '../util/api.helpers'
import {
  formatCurrencyValue,
  formatEthCurrency,
  formatPts
} from '../util/numbers.helpers'
import {
  SALE_PROCESS_STEP,
  DEFAULT_CONTAINER_STATE
} from '../constants/containerStates'
import SuccessPopup from '../components/popups/SuccessPopup'
import ConnectAccountPopup from '../components/popups/ConnectAccountPopup'
import {
  stakingReducer,
  stakingInitialState,
  STAKING_PROFILE_DISPATCH
} from '../store/reducers/staking'
import { changeStakingState } from '../store/actions/staking'
import { SUCCESS_POPUP_TYPE } from '../constants/popup'
import usePrevious from '../hooks/usePrevious'
import RewardsBoostersPopup from '../components/popups/RewardsBoostersPopup'
import DepositedSuccessPopup from '../components/popups/DepositedSuccessPopup'
import { LEADERBOARD_URL } from '../constants/navigation'
import { POLL_ACCOUNT_WALLET_INFO_API_TIME } from '../constants/app'

const chainId = getChain().id

const CommunitySaleContainer = ({
  onClickRefresh,
  mainProfile,
  mainDispatch,
  saleEnded
}) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [stakingProfile, stakingDispatch] = useReducer(
    stakingReducer,
    stakingInitialState
  )
  const [showResults, setShowResults] = useState(false)

  const [popupState, setPopupState] = useState({
    id: DEFAULT_CONTAINER_STATE.NONE,
    data: null
  })

  const location = useLocation()

  useEffect(() => {
    if (location) {
      setShowResults(false)
    }
  }, [location])

  const timelineInterval = useRef(null)

  useEffect(() => {
    getRecentDepositsAPI(mainDispatch)
    if (!saleEnded) {
      timelineInterval.current = setInterval(() => {
        getRecentDepositsAPI(mainDispatch)
      }, POLL_ACCOUNT_WALLET_INFO_API_TIME)
    } else {
      clearTimelineInterval()
    }
    return () => {
      clearTimelineInterval()
    }
  }, [saleEnded])

  const clearTimelineInterval = () => {
    clearInterval(timelineInterval.current)
  }

  const { address, status } = useAccount()

  const { data: walletBalance } = useBalance({
    address: address,
    chainId
  })

  const isWalletConnected =
    status === WALLET_ACCOUNT_STATUS.CONNECTED && address

  const {
    data: addFundsHash,
    isPending: addFundsPending,
    error: addFundsErr,
    writeContract: addFundsToContract
  } = useWriteContract()

  const {
    isLoading: isDepositProcessing,
    error: depositFundsErr,
    isSuccess: isDepositConfirmed
  } = useWaitForTransactionReceipt({
    hash: addFundsHash
  })

  useEffect(() => {
    if (addFundsPending || isDepositProcessing) {
      dispatchAction(MAIN_PROFILE_DISPATCH.DEPOSIT_PROCESSING, true)
    }
  }, [addFundsPending, isDepositProcessing])

  const {
    data: refundFundsHash,
    isPending: refundFundsPending,
    error: refundFundsErr,
    writeContract: refundFundsFromContract
  } = useWriteContract()

  const { isLoading: isRefundProcessing, isSuccess: isRefundConfirmed } =
    useWaitForTransactionReceipt({
      hash: refundFundsHash
    })

  useEffect(() => {
    if (refundFundsPending || isRefundProcessing) {
      dispatchAction(MAIN_PROFILE_DISPATCH.REFUND_PROCESSING, true)
    }
  }, [refundFundsPending, isRefundProcessing])

  useEffect(() => {
    if (refundFundsErr) {
      dispatchAction(MAIN_PROFILE_DISPATCH.ERROR, {
        refund: buildTransactionErrorMessage(refundFundsErr.shortMessage)
      })
    }
  }, [refundFundsErr])

  useEffect(() => {
    if (isDepositConfirmed || isRefundConfirmed) {
      onClickRefresh()
    }
  }, [isDepositConfirmed, isRefundConfirmed])

  const prevPointsCount = usePrevious(
    mainProfile.points + mainProfile.finalPoints
  )
  useEffect(() => {
    if (stakingProfile.claimedStake) {
      setPopupState({
        id: SALE_PROCESS_STEP.SUCCESS,
        data: {
          type: SUCCESS_POPUP_TYPE.CLAIMED,
          value: formatPts(prevPointsCount)
        }
      })
    }
  }, [stakingProfile.claimedStake])

  useEffect(() => {
    if (isDepositConfirmed) {
      dispatchAction(MAIN_PROFILE_DISPATCH.ERROR, {})
      dispatchAction(MAIN_PROFILE_DISPATCH.AMOUNT, '')
      setPopupState({
        id: SALE_PROCESS_STEP.DEPOSIT_SUCCESS,
        data: {
          value: parseFloat(mainProfile.amount)
        }
      })
    }
  }, [isDepositConfirmed])

  const prevUserDeposit = usePrevious(mainProfile.userDeposit)

  useEffect(() => {
    if (isRefundConfirmed) {
      dispatchAction(MAIN_PROFILE_DISPATCH.ERROR, {})
      setPopupState({
        id: SALE_PROCESS_STEP.SUCCESS,
        data: {
          type: SUCCESS_POPUP_TYPE.REFUNDED,
          value: formatEthCurrency(prevUserDeposit)
        }
      })
    }
  }, [isRefundConfirmed])

  useEffect(() => {
    if (addFundsErr || depositFundsErr) {
      dispatchAction(MAIN_PROFILE_DISPATCH.ERROR, {
        [MAIN_PROFILE_DISPATCH.AMOUNT]: buildTransactionErrorMessage(
          addFundsErr.shortMessage || depositFundsErr.shortMessage
        )
      })
    }
  }, [addFundsErr, depositFundsErr])

  const {
    signMessage,
    error: signMsgErr,
    data: signMsgData,
    isPending: signMsgPending
  } = useSignMessage()

  useEffect(() => {
    if (signMsgPending) {
      dispatchStakingAction(
        STAKING_PROFILE_DISPATCH.CLAIM_STAKE_PROCESSING,
        true
      )
    }
  }, [signMsgPending])

  useEffect(() => {
    if (signMsgErr) {
      dispatchStakingAction(STAKING_PROFILE_DISPATCH.ERROR, {
        claimStake: buildTransactionErrorMessage(signMsgErr.shortMessage)
      })
    }
  }, [signMsgErr])

  useEffect(() => {
    if (signMsgData) {
      claimStakeAPI(
        stakingDispatch,
        address,
        signMsgData,
        stakingProfile.accountNumber
      )
    }
  }, [signMsgData])

  // useEffect(() => {
  //   if (isWalletConnected && ENABLE_SALE_RESULTS && saleEnded) {
  //     getClaimStatusAPI(stakingDispatch, address)
  //   }
  // }, [isWalletConnected])

  const handleNoPopup = () => {
    setPopupState({ id: DEFAULT_CONTAINER_STATE.NONE })
    // mainDispatch(resetMainState())
  }

  const handleClickRefund = () => {
    dispatchAction(MAIN_PROFILE_DISPATCH.REFUND_PROCESSING, true)
    refundFundsFromContract({
      ...wagmiContractConfig,
      functionName: 'withdrawFunds',
      args: []
    })
  }

  const handleClickDeposit = () => {
    setPopupState({ id: SALE_PROCESS_STEP.DEPOSIT })
  }

  const handleOnCancelPopup = () => {
    switch (popupState.id) {
      case SALE_PROCESS_STEP.DEPOSIT:
        handleNoPopup()
        dispatchAction(MAIN_PROFILE_DISPATCH.AMOUNT, '')
        dispatchAction(MAIN_PROFILE_DISPATCH.ERROR, {})
        break

      case SALE_PROCESS_STEP.CONNECT_ACCOUNT:
        handleNoPopup()
        dispatchStakingAction(STAKING_PROFILE_DISPATCH.ACCOUNT_NUMBER, '')
        dispatchStakingAction(STAKING_PROFILE_DISPATCH.ERROR, {})
        break

      case SALE_PROCESS_STEP.REWARDS_BOOSTERS:
        navigate(LEADERBOARD_URL)
        break

      default:
        handleNoPopup()
    }
  }

  const handleOnSubmitPopup = () => {
    switch (popupState.id) {
      case SALE_PROCESS_STEP.DEPOSIT:
        if (!saleEnded) {
          dispatchAction(MAIN_PROFILE_DISPATCH.ERROR, {})
          const { amount } = mainProfile
          const errors = checkValidations({
            amount: {
              value: amount,
              rules: [
                requiredRule,
                positiveDecimalRule,
                maxValueRule(formatCurrencyValue(walletBalance.formatted))
              ]
            }
          })
          dispatchAction(MAIN_PROFILE_DISPATCH.DEPOSIT_PROCESSING, true)
          if (containsNoValues(errors)) {
            addFundsToContract({
              ...wagmiContractConfig,
              functionName: 'addFunds',
              args: [],
              value: parseEther(amount)
            })
          } else {
            dispatchAction(MAIN_PROFILE_DISPATCH.ERROR, errors)
          }
        }
        break

      case SALE_PROCESS_STEP.CONNECT_ACCOUNT:
        dispatchStakingAction(STAKING_PROFILE_DISPATCH.ERROR, {})
        const { accountNumber } = stakingProfile
        const errors = checkValidations({
          accountNumber: {
            value: accountNumber,
            rules: [requiredRule, UUIDRule]
          }
        })
        if (containsNoValues(errors)) {
          dispatchStakingAction(
            STAKING_PROFILE_DISPATCH.CLAIM_STAKE_PROCESSING,
            true
          )
          signMessage({ message: accountNumber })
        } else {
          dispatchStakingAction(STAKING_PROFILE_DISPATCH.ERROR, errors)
        }
        break

      case SALE_PROCESS_STEP.DEPOSIT_SUCCESS:
        openBlankRedirectPage(shareXPostUrl(address, addFundsHash))
        handleNoPopup()
        break

      default:
        handleNoPopup()
        break
    }
  }

  const handleClickResults = () => {
    setShowResults(true)
  }

  const handleOnRedeemPoints = () => {
    setPopupState({ id: SALE_PROCESS_STEP.CONNECT_ACCOUNT })
  }

  const dispatchAction = (attr, value) => {
    mainDispatch(changeMainState(attr, value))
  }

  const dispatchStakingAction = (attr, value) => {
    stakingDispatch(changeStakingState(attr, value))
  }

  const handleRewardMultipliers = () => {
    setPopupState({ id: SALE_PROCESS_STEP.REWARDS_BOOSTERS })
  }

  const renderPopup = () => {
    switch (popupState.id) {
      case SALE_PROCESS_STEP.DEPOSIT:
        return (
          <DepositPopup
            t={t}
            onClose={handleOnCancelPopup}
            onSubmit={handleOnSubmitPopup}
            mainProfile={mainProfile}
            dispatchAction={dispatchAction}
            saleEnded={saleEnded}
            walletBalance={walletBalance}
          />
        )

      case SALE_PROCESS_STEP.CONNECT_ACCOUNT:
        return (
          <ConnectAccountPopup
            t={t}
            onClose={handleOnCancelPopup}
            onSubmit={handleOnSubmitPopup}
            stakingProfile={stakingProfile}
            dispatchAction={dispatchStakingAction}
          />
        )

      case SALE_PROCESS_STEP.SUCCESS:
        return (
          <SuccessPopup
            t={t}
            onSubmit={handleOnSubmitPopup}
            {...popupState?.data}
          />
        )

      case SALE_PROCESS_STEP.DEPOSIT_SUCCESS:
        return (
          <DepositedSuccessPopup
            t={t}
            onSubmit={handleOnSubmitPopup}
            onClose={handleOnCancelPopup}
            address={address}
            mainProfile={mainProfile}
            {...popupState?.data}
          />
        )

      case SALE_PROCESS_STEP.REWARDS_BOOSTERS:
        return (
          <RewardsBoostersPopup
            t={t}
            onSubmit={handleOnSubmitPopup}
            onClose={handleOnCancelPopup}
            onAutoClose={handleNoPopup}
            mainProfile={mainProfile}
            saleEnded={saleEnded}
          />
        )

      default:
        break
    }
  }
  return (
    <>
      {['connecting', 'reconnecting'].includes(status) ? (
        <Loading />
      ) : (
        <CommunitySale
          t={t}
          mainProfile={mainProfile}
          saleRunningProps={{
            onClickDeposit: handleClickDeposit,
            onClickResults: handleClickResults,
            onClickRewardMultipliers: handleRewardMultipliers,
            address,
            contractAddress: wagmiContractConfig.address
          }}
          saleResultsProps={{
            onClickRefund: handleClickRefund,
            claimStakeProps: {
              onRedeemPoints: handleOnRedeemPoints,
              stakingProfile
            }
          }}
          isWalletConnected={isWalletConnected}
          address={address}
          saleEnded={saleEnded}
          showResults={showResults}
          dispatchAction={dispatchAction}
        />
      )}
      {renderPopup()}
    </>
  )
}

export default CommunitySaleContainer
